import { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button, Col, Form, Input, Row, message,
} from 'antd';
import { useIntl, FormattedMessage } from 'react-intl';
import { JobsApplyContext, UserContext } from 'contexts';
import { MIN_PASSWORD_LENGTH } from 'constants/formValidation';
import PageTitle from 'components/PageTitle';
import { ReactComponent as IconEmail } from 'assets/icons/email.svg';
import { ReactComponent as IconPassword } from 'assets/icons/password.svg';
import { ReactComponent as IconShowPassword } from 'assets/icons/show-password.svg';
import { ReactComponent as Arrow } from 'assets/icons/arrow-left.svg';
import { GTM_EVENTS } from 'constants/analytics';

import { fbAnalytics } from 'utils/analytics';
import styles from './SignInEmailPage.module.scss';
import EmployerTitleSection from '../EmployerCreationPage/components/EmployerTitleSection';
import LoadingOverlay from '../../components/LoadingOverlay';
import { sendEmail } from 'services/post/authEmail';

// [todo] refactor fields validation
const SignInEmailPage = () => {
  const {
    lastSelectedJob, user, email: enteredEmail, setEmail,
  } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [formLayout] = useState('vertical');
  const [canSubmit, setCanSubmit] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  const pressedLoginURI = location.state?.from === 'pressedLogin';
  const pressedEmployerURI = location.state?.from === 'pressedEmployerCreation';

  const sendVerificationEmail = async (email) => {
    setIsLoadingEmail(true);
    try {
      const response = await sendEmail(email);
      if (!response.ok) {
        throw new Error('Failed to send verification email');
      }
      message.success('Verification email sent. Please check your inbox.');
    } catch (error) {
      message.error('Error sending verification email. Please try again.');
      console.error('Error sending verification email:', error);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  useEffect(() => {
    if (enteredEmail) {
      form.setFieldsValue({
        email: enteredEmail,
      });
      setCanSubmit(true);
    }
  }, [enteredEmail]);

  useEffect(() => {
    if (showPasswordField) setCanSubmit(false);
  }, [showPasswordField]);

  const handleSubmit = (e) => {
    setIsLoadingEmail(true);
    // ToDo move to context
    localStorage.setItem('email', form.getFieldValue('email'));
    fbAnalytics(GTM_EVENTS.SIGN_IN_EMAIL, { lastSelectedJob });

    e.preventDefault();
    const { email, password } = form.getFieldsValue(['email', 'password']);

    sendVerificationEmail(email);
  };

  const handleTroubleSigningIn = () => {
    const email = form.getFieldValue('email');
  };

  const handleFieldsChange = () => {
    const fieldsError = form.getFieldsError();
    const { password } = form.getFieldsValue(['email', 'password']);
    const formErrors = fieldsError.some((field) => field.errors.length);

    if (
      (!showPasswordField && formErrors)
      || (showPasswordField && (formErrors || !password))
    ) {
      setCanSubmit(false);
    } else {
      setCanSubmit(true);
    }
  };

  const formItemLayout = formLayout === 'horizontal'
    ? {
      labelCol: {
        span: 4,
      },
      wrapperCol: {
        span: 14,
      },
    }
    : null;

  const buttonItemLayout = formLayout === 'horizontal'
    ? {
      wrapperCol: {
        span: 24,
      },
    }
    : null;

  return (
    <>
      <LoadingOverlay isLoading={isLoadingEmail} />
      <PageTitle>
        <h2>
          {!pressedLoginURI && !pressedEmployerURI && (
            <FormattedMessage
              // eslint-disable-next-line no-nested-ternary
              id={showPasswordField ? 'sign_in.sign_in' : (lastSelectedJob?.title ? 'job.apply.you_are_apply' : 'sign_up.title')}
              values={{ jobTitle: lastSelectedJob?.title }}
            />
          )}
        </h2>
        {pressedEmployerURI && !pressedLoginURI && <EmployerTitleSection />}
      </PageTitle>
      <section>
        <div className="container container-small">
          <div className="form-holder" data-qa="enterYourEmailWindow">
            <Form
              className="form small-form"
              {...formItemLayout}
              layout={formLayout}
              form={form}
              onSubmitCapture={handleSubmit}
              requiredMark={false}
              onFieldsChange={handleFieldsChange}
            >
              {showPasswordField ? '' : <h3 className="title-form"><FormattedMessage id="sign_in.enter_email" /></h3>}
              <Form.Item
                name="email"
                label={<FormattedMessage id="form.email" />}
                onClick={() => {
                  fbAnalytics(GTM_EVENTS.JOIN_ENTER_EMAIL, { lastSelectedJob });
                }}
                rules={[
                  {
                    required: true,
                    message: <FormattedMessage id="validation.email" />,
                  },
                  {
                    type: 'email',
                    message: <FormattedMessage id="validation.email.valid" />,
                  },
                ]}
              >
                <Input
                  data-qa="emailInputField"
                  suffix={<IconEmail className="icon" disabled={showPasswordField} />}
                  onChange={(e) => setEmailValue(e.target.value)}
                />
              </Form.Item>
              {showPasswordField ? (
                <Form.Item
                  name="password"
                  label={<FormattedMessage id="form.password" />}
                  onChange={() => {
                    fbAnalytics(GTM_EVENTS.JOIN_ENTER_PASSWORD, { lastSelectedJob });
                  }}
                  rules={[
                    {
                      required: true,
                      message: <FormattedMessage id="validation.password" />,
                    },
                    {
                      min: MIN_PASSWORD_LENGTH,
                      message: <FormattedMessage
                        id="validation.password.length"
                        values={{ minLength: MIN_PASSWORD_LENGTH }}
                      />,
                    },
                  ]}
                >
                  <Input.Password
                    data-qa="passwordInputField"
                    iconRender={(showPassword) => (showPassword
                      ? <IconPassword className="icon" />
                      : <IconShowPassword className="icon" />)}
                  />
                </Form.Item>
              ) : null}
              <Row gutter={[12, 0]} className="text-left form-button-holder">
                <Col span={12}>
                  <Form.Item>
                    <Button
                      type="ghost"
                      className="btn btn-transparent icon-link"
                      data-qa="enterYourEmailBackButton"
                      onClick={() => navigate(-1)}
                      block
                    >
                      <Arrow />
                      <FormattedMessage id="form.button.back" />
                    </Button>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...buttonItemLayout} className="text-right">
                    <Button
                      type="primary"
                      className="btn"
                      htmlType="submit"
                      data-qa={showPasswordField ? 'enterYourEmailSignInButton' : 'enterYourEmailNextButton'}
                      disabled={!canSubmit}
                      onClick={() => {
                        setEmail(emailValue);
                      }}
                      block
                    >
                      {showPasswordField
                        ? <FormattedMessage id="form.button.sign_in" />
                        : <FormattedMessage id="form.button.next" />}
                    </Button>
                  </Form.Item>
                </Col>
                {showPasswordField ? (
                  <div className={`form-block-bottom ${styles.showPasswordButton}`}>
                    <Button
                      block
                      type="link"
                      className="link-btn"
                      data-qa="troubleSigningInButton"
                      onClick={handleTroubleSigningIn}
                    >
                      <FormattedMessage id="form.button.trouble" />
                    </Button>
                  </div>
                ) : null}
              </Row>
            </Form>
          </div>
        </div>
      </section>
    </>
  );
};

export default SignInEmailPage;
